import type { BriefInput } from '../types';
import { emptyBriefInput } from './draft';

/**
 * Short-lived storage for the in-progress brief on the start page.
 *
 * Signing in with Google is a full-page redirect, so anything typed before
 * signing in would otherwise be lost on the way back. Persisting it here means
 * the user can paste first and sign in second.
 *
 * `sessionStorage` is deliberate: it is scoped to the tab and cleared when the
 * tab closes. Only brief content is stored — never credentials.
 */

const KEY = 'apr.briefInput.v1';

export function saveBriefInput(input: BriefInput): void {
  try {
    sessionStorage.setItem(KEY, JSON.stringify(input));
  } catch {
    // Private browsing or a full quota — losing the draft is preferable to
    // breaking the form, so this is intentionally swallowed.
  }
}

export function loadBriefInput(): BriefInput | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as Partial<BriefInput>;
    // Merge onto a fresh object so a stored value from an older shape cannot
    // produce undefined fields.
    return {
      ...emptyBriefInput(),
      ...parsed,
      businessType: parsed.businessType === 'DIRECT' ? 'DIRECT' : 'AGENCY',
    };
  } catch {
    return null;
  }
}

export function clearBriefInput(): void {
  try {
    sessionStorage.removeItem(KEY);
  } catch {
    // Nothing useful to do if storage is unavailable.
  }
}
